/**
 * Onboarding wizard data: industries, brand-value sport presets, per-industry
 * wording and the four brand-indicator definitions (statement-based scoring).
 *
 * Single source of truth for the Brand Values wizard (opened on the first new
 * valuation and from the settings page). Deliberately a static data file, not a DB
 * table: statements/bands are tightly coupled to the wizard logic, and these
 * are global product defaults — the org's CHOSEN values live on the
 * organization table (default_brand_popularity/csr/culture/loyalty).
 *
 * Wording placeholders: {subject}, {audience} and {region} in questions and
 * statements are replaced per industry via applyIndustryWording() so "brand"
 * never appears where it doesn't fit.
 */

// ---------------------------------------------------------------------------
// Industries (ids match LABEL_EXAMPLES in lib/platform/hooks/use-alias.ts)
// ---------------------------------------------------------------------------

export type WizardIndustryId = 'sports' | 'creator' | 'charity' | 'business' | 'events';

export interface WizardIndustry {
  id: WizardIndustryId;
  name: string;
  description: string;
}

export const WIZARD_INDUSTRIES: readonly WizardIndustry[] = [
  { id: 'sports', name: 'Sports', description: 'Clubs, teams, leagues, athletes' },
  {
    id: 'creator',
    name: 'Creator & Media',
    description: 'Influencers, streamers, podcasts, media',
  },
  { id: 'charity', name: 'Charity & NGO', description: 'Nonprofits, foundations, causes' },
  {
    id: 'business',
    name: 'Company & Brand',
    description: 'A company valuing its own brand or sponsorships',
  },
  { id: 'events', name: 'Events & Venues', description: 'Festivals, conferences, arenas' },
];

// ---------------------------------------------------------------------------
// Brand indicators
// ---------------------------------------------------------------------------

export type BrandIndicatorKey = 'popularity' | 'csr' | 'culture' | 'loyalty';

export type BrandValues = Record<BrandIndicatorKey, number>;

/**
 * "Don't use Brand Values" — neutral defaults. 4 gives a qualitative
 * multiplier of exactly 1.0 (formula: 0.5 + (avg - 1) * (1.5 / 9)), so the
 * brand values do not affect the valuation result.
 */
export const RECOMMENDED_BRAND_VALUES: BrandValues = {
  popularity: 4,
  csr: 4,
  culture: 4,
  loyalty: 4,
};

export interface BrandIndicatorOption {
  /** Plain-language statement (may contain wording placeholders) */
  label: string;
  /** Value set when the statement is picked */
  score: number;
  /** Inclusive slider range that highlights this statement */
  range: readonly [number, number];
}

export interface BrandIndicatorDefinition {
  key: BrandIndicatorKey;
  title: string;
  /** Question shown above the statements (may contain wording placeholders) */
  question: string;
  options: readonly BrandIndicatorOption[];
}

export const BRAND_INDICATORS: readonly BrandIndicatorDefinition[] = [
  {
    key: 'popularity',
    title: 'Popularity',
    question: 'How well-known is your {subject}?',
    options: [
      { label: 'International — recognized beyond our country', score: 9, range: [9, 10] },
      { label: 'National — a household name at home', score: 7, range: [7, 8] },
      { label: 'Regional — well known in our {region}', score: 5, range: [5, 6] },
      { label: 'Niche — known mainly by our own {audience}', score: 3, range: [1, 4] },
    ],
  },
  {
    key: 'csr',
    title: 'CSR',
    question:
      'How visible is your social responsibility work (community, charity, sustainability)?',
    options: [
      {
        label: 'Central to who we are — structured programs with measurable impact',
        score: 9,
        range: [9, 10],
      },
      {
        label: 'Regular — ongoing community programs & charity partnerships',
        score: 7,
        range: [7, 8],
      },
      { label: 'Occasional — a few initiatives or events per year', score: 5, range: [5, 6] },
      { label: 'Little or none yet', score: 3, range: [1, 4] },
    ],
  },
  {
    key: 'culture',
    title: 'Culture',
    question: 'How strong is the history and identity around your {subject}?',
    options: [
      {
        label: 'Iconic — rich history and traditions people know by heart',
        score: 9,
        range: [9, 10],
      },
      {
        label: 'Strong — clear values and traditions our {audience} connect with',
        score: 7,
        range: [7, 8],
      },
      {
        label: 'Developing — some traditions, identity still taking shape',
        score: 5,
        range: [5, 6],
      },
      { label: 'New — limited heritage so far', score: 3, range: [1, 4] },
    ],
  },
  {
    key: 'loyalty',
    title: 'Loyalty',
    question: 'How loyal are your {audience} — do they stay through ups and downs?',
    options: [
      {
        label: 'Die-hard — they stay no matter what, high renewal rates',
        score: 9,
        range: [9, 10],
      },
      { label: 'Strong — stable engagement year after year', score: 7, range: [7, 8] },
      { label: 'Moderate — engagement follows success and results', score: 5, range: [5, 6] },
      { label: 'Casual — mostly occasional or event-driven interest', score: 3, range: [1, 4] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Per-industry wording
// ---------------------------------------------------------------------------

export interface IndustryWording {
  subject: string;
  audience: string;
  region: string;
}

/** Fallback when no industry was chosen (setup wizard skipped) */
export const NEUTRAL_WORDING: IndustryWording = {
  subject: 'organization',
  audience: 'audience',
  region: 'region',
};

export const INDUSTRY_WORDING: Record<WizardIndustryId, IndustryWording> = {
  sports: { subject: 'club', audience: 'fans', region: 'region or league' },
  creator: { subject: 'channel', audience: 'followers', region: 'niche or platform' },
  charity: { subject: 'organization', audience: 'supporters', region: 'region or cause area' },
  business: { subject: 'brand', audience: 'customers', region: 'region or market' },
  events: { subject: 'event', audience: 'guests', region: 'region or scene' },
};

/** Replace {subject}/{audience}/{region} placeholders for an industry. */
export function applyIndustryWording(text: string, industry?: WizardIndustryId | null): string {
  const wording = (industry && INDUSTRY_WORDING[industry]) || NEUTRAL_WORDING;
  return text
    .replaceAll('{subject}', wording.subject)
    .replaceAll('{audience}', wording.audience)
    .replaceAll('{region}', wording.region);
}

// ---------------------------------------------------------------------------
// Sport presets (Brand Values wizard, sports orgs only)
// ---------------------------------------------------------------------------

export interface SportPreset {
  id: string;
  /** Short name used in the wizard chips */
  name: string;
  values: BrandValues;
  /** Typical range per indicator — the shaded slider band in the wizard */
  bands: Record<BrandIndicatorKey, readonly [number, number]>;
}

/**
 * Values match the former hardcoded Quick Fill presets on the settings page
 * (removed in favor of the wizard); bands match the ranges shown in the
 * settings "Industry Sector Typical Ranges" tooltip.
 */
export const SPORT_PRESETS: readonly SportPreset[] = [
  {
    id: 'premier-league',
    name: 'Football — top league',
    values: { popularity: 9, csr: 7, culture: 9, loyalty: 9 },
    bands: { popularity: [8, 10], csr: [6, 8], culture: [8, 10], loyalty: [8, 10] },
  },
  {
    id: 'championship',
    name: 'Football — lower league',
    values: { popularity: 7, csr: 6, culture: 8, loyalty: 8 },
    bands: { popularity: [6, 8], csr: [5, 7], culture: [7, 9], loyalty: [7, 9] },
  },
  {
    id: 'basketball',
    name: 'Basketball',
    values: { popularity: 7, csr: 7, culture: 7, loyalty: 7 },
    bands: { popularity: [6, 8], csr: [6, 8], culture: [6, 8], loyalty: [6, 8] },
  },
  {
    id: 'tennis',
    name: 'Tennis',
    values: { popularity: 6, csr: 8, culture: 7, loyalty: 6 },
    bands: { popularity: [5, 7], csr: [7, 9], culture: [6, 8], loyalty: [5, 7] },
  },
  {
    id: 'motor-racing',
    name: 'Motor racing',
    values: { popularity: 8, csr: 6, culture: 8, loyalty: 9 },
    bands: { popularity: [7, 9], csr: [5, 7], culture: [7, 9], loyalty: [8, 10] },
  },
  {
    id: 'olympic',
    name: 'Olympic / federation',
    values: { popularity: 5, csr: 9, culture: 9, loyalty: 7 },
    bands: { popularity: [4, 6], csr: [8, 10], culture: [8, 10], loyalty: [6, 8] },
  },
  {
    id: 'esports',
    name: 'Esports',
    values: { popularity: 7, csr: 5, culture: 8, loyalty: 9 },
    bands: { popularity: [6, 8], csr: [4, 6], culture: [7, 9], loyalty: [8, 10] },
  },
  {
    id: 'other',
    name: 'Other / not sure',
    values: { popularity: 5, csr: 5, culture: 5, loyalty: 5 },
    bands: { popularity: [3, 7], csr: [3, 7], culture: [3, 7], loyalty: [3, 7] },
  },
];
